import { useState, type FormEvent } from "react";
import { GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";
import { Backdrop } from "../components/Backdrop";

const MESSAGES: Record<string, string> = {
    "auth/invalid-credential": "That email and password do not match.",
    "auth/wrong-password": "That email and password do not match.",
    "auth/user-not-found": "There is no Swana account with that email.",
    "auth/invalid-email": "Enter a valid email address.",
    "auth/too-many-requests": "Too many attempts. Wait a few minutes and try again.",
    "auth/network-request-failed": "No connection. Check your network and try again.",
    "auth/popup-closed-by-user": "The Google window was closed before signing in.",
};

const messageFor = (err: unknown, fallback: string) => MESSAGES[(err as { code?: string })?.code ?? ""] ?? fallback;

/**
 * Password and Google only. Coaches are existing Swana accounts attached to a
 * club by Swana or club staff; there is nothing to register here.
 */
export default function SignIn() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const run = async (work: () => Promise<void>, fallback: string) => {
        setBusy(true); setError(null); setNotice(null);
        try { await work(); } catch (err) { setError(messageFor(err, fallback)); } finally { setBusy(false); }
    };

    const submit = (e: FormEvent) => {
        e.preventDefault();
        void run(() => signInWithEmailAndPassword(auth, email.trim(), password).then(() => undefined), "Sign in failed. Please try again.");
    };
    const google = () => void run(() => signInWithPopup(auth, new GoogleAuthProvider()).then(() => undefined), "Google sign-in failed. Please try again.");
    const reset = () => {
        if (!email.trim()) { setError("Enter your email first, then tap reset."); return; }
        void run(async () => { await sendPasswordResetEmail(auth, email.trim()); setNotice("Reset email sent. Check your inbox."); }, "Could not send the reset email.");
    };

    return (
        <div className="signin">
            <Backdrop />
            <div className="signin-inner">
                <header className="signin-hero">
                    <div className="brand"><img src="/swana-logo.png" alt="" width={36} height={36} /><span>Swana Coach</span></div>
                    <h1 className="hero-title">Every point.<br /><span>Ready to coach.</span></h1>
                    <p className="hero-sub">Players send you the moments that matter from their Swana recordings. Review them, mark what you see and send it back.</p>
                </header>
                <form className="card glass stack" onSubmit={submit}>
                    <h2 className="signin-title">Sign in</h2>
                    <p className="muted">Use the same account you use in the Swana app.</p>
                    <label>Email<input type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
                    <label>Password<input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
                    {error && <p className="error-text" role="alert">{error}</p>}
                    {notice && <p className="ok-text">{notice}</p>}
                    <button className="btn primary" type="submit" disabled={busy}>{busy ? "Signing in" : "Sign in"}</button>
                    <button className="btn" type="button" onClick={google} disabled={busy}>Continue with Google</button>
                    <button className="btn ghost small" type="button" onClick={reset} disabled={busy}>Forgotten your password?</button>
                    <p className="muted small fine-print">Coaches are set up by their club or by Swana. If you coach at a Swana venue and cannot get in, contact Swana.</p>
                </form>
            </div>
        </div>
    );
}
